// src/pages/customer/CustomerList.tsx

import React, { useState } from "react";
// 1. EDataTable bileşenini içe aktar
import { EDataTable } from "../../../components/EDataTable";
import { TableActions } from "../../../components/TableActions";
// Müşteri yönetimi için store'u içe aktar
import { useCustomerStore } from "../../../store/customerStore";
// PrimeReact Column (sadece sütunları tanımlamak için)
import { Column } from "primereact/column";
import { IconButton, TextButton } from "../../../components/ButtonComponents";
import { Edit, Trash2, FileText } from "lucide-react";
// Müşteri ekleme/düzenleme dialogu
import { CustomerDialog } from "./CustomerDialog";
import { toast } from "react-toastify";
// Müşteri tipi
import { Customer } from "../../../types/customer";
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { useApiErrorHandler } from "../../../hooks/useApiErrorHandler";
import { DataTableColumn, SimpleFilter } from "../../../types";
import { AccountStatementDialog } from "./AccountStatementDialog"; // Yeni dialogu import et
import { VisitTemplateDialog } from "../visit/VisitTemplateDialog";
import { useVisitManagementStore } from "../../../store/visitStore";
import { VisitTemplate } from "../../../types/visit";
import { CalendarDays } from "lucide-react";
export const CustomerList = () => {

    // 2. Müşteri store hook'unu alıyoruz
    const store = useCustomerStore();
    const { createItem, updateItem, deleteItem, exportExcel, importExcel } = store; // 🌟 Excel fonksiyonlarını store'dan çektik
    const { handleApiError } = useApiErrorHandler();

    // --- STATE'LER (Sadece CRUD ve Dialog için) ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [customerToEdit, setCustomerToEdit] = useState<Customer | null>(null);
    const [isStatementOpen, setIsStatementOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<{ id: number; name: string } | null>(null);

    // Rota atama states
    const [isRouteModalOpen, setIsRouteModalOpen] = useState(false);
    const [customerForRoute, setCustomerForRoute] = useState<Customer | null>(null);
    const { templates: templateStore } = useVisitManagementStore();

    const handleViewStatement = (customer: Customer) => {
        setSelectedCustomer({ id: customer.id, name: customer.name });
        setIsStatementOpen(true);
    };

    const handleAddToRoute = (customer: Customer) => {
        setCustomerForRoute(customer);
        setIsRouteModalOpen(true);
    };

    const handleSaveNewRoute = async (data: Partial<VisitTemplate>) => {
        try {
            await templateStore.createItem(data as any);
            toast.success("Rota başarıyla eklendi");
            setIsRouteModalOpen(false);
        } catch (err) {
            handleApiError(err);
        }
    };
    // --- ACTION HANDLERS ---
    const handleNew = () => {
        setCustomerToEdit(null);
        setIsModalOpen(true);
    };

    const handleEdit = (customer: Customer) => {
        setCustomerToEdit(customer);
        setIsModalOpen(true);
    };
    // --- DataTable body templates (Aynı Kalır) ---
    const balanceBodyTemplate = (customer: Customer) => {
        const format = (val: number, cur: string) =>
            new Intl.NumberFormat("tr-TR", { style: "currency", currency: cur }).format(val || 0);

        return (
            <div className="flex flex-col gap-1 py-1">
                <span className="font-bold  ">{format(customer.balance, "TRY")}</span>
                <div className="flex gap-2 text-[10px] font-semibold">
                    <span className="text-blue-600 bg-blue-50 px-1 rounded">USD: {format(customer.balanceUSD ?? 0, "USD")}</span>
                    <span className="text-purple-600 bg-purple-50 px-1 rounded">EUR: {format(customer.balanceEUR ?? 0, "EUR")}</span>
                </div>
            </div>
        );
    };
    const dealerBodyTemplate = (customer: Customer) => {
        const dealer = customer.isDealer ? "Bayi" : "Müşteri";
        return <span className="text-gray-700">{dealer}</span>;
    };
    const { confirm, modal: confirmModal } = useConfirmModal();

    const handleDeleteClick = async (customer: Customer) => {
        if ((customer.source || "").toUpperCase() === "SYNC") {
            toast.warn("Entegrasyon (SYNC) ile aktarılan cariler silinemez.");
            return;
        }

        // Müşteri adı yerine genellikle "name" veya "companyName" kullanılır.
        // Burada 'name' alanının olduğunu varsayalım.
        const ok = await confirm(`${customer.name} adlı müşteriyi silmek istiyor musun?`);

        if (ok) {
            try {
                // deleteItem, başarılı olursa otomatik olarak listeyi günceller
                await deleteItem(customer.id);
                toast.success("Müşteri silindi");
            } catch (error) {
                handleApiError(error);
            }
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCustomerToEdit(null);
    };

    // Customer tipinde 'id' hariç veriyi alır
    const handleSaveCustomer = async (customerData: Omit<Customer, "id">) => {
        // Veri temizleme veya düzenleme gerekiyorsa buraya eklenir
        const cleanedData: any = { ...customerData };

        try {
            if (customerToEdit) {
                // Düzenleme
                await updateItem(customerToEdit.id, cleanedData);
                toast.success(`Müşteri Güncellendi: ${cleanedData.name}`);
            } else {
                // Oluşturma
                await createItem(cleanedData);
                toast.success(`Yeni Müşteri Eklendi: ${cleanedData.name}`);
            }
            // createItem/updateItem listeyi zaten yeniler.

        } catch (error) {
            handleApiError(error);
        }
        handleCloseModal(); // Başarılı kayıttan sonra dialogu kapat
    };


    // --- DataTable body templates ---

    // Örnek: Telefon numarası formatlama (varsayımsal)
    const phoneBodyTemplate = (customer: Customer) => (
        <span className="font-mono text-gray-700">
            {customer.phone || "-"}
        </span>
    );

    const actionBodyTemplate = (customer: Customer) => {
        const isSync = (customer.source || "").toUpperCase() === "SYNC";

        return (
            <TableActions
                actions={[
                    {
                        icon: FileText,
                        colorClass: "text-emerald-600 hover:bg-emerald-50",
                        title: "Cari Ekstre",
                        onClick: () => handleViewStatement(customer)
                    },
                    {
                        icon: CalendarDays,
                        colorClass: "text-orange-500 hover:bg-orange-50",
                        title: "Rotaya Ekle",
                        onClick: () => handleAddToRoute(customer)
                    },
                    {
                        icon: Edit,
                        colorClass: "text-blue-600 hover:bg-blue-50",
                        title: "Düzenle",
                        onClick: () => handleEdit(customer)
                    },
                    {
                        icon: Trash2,
                        colorClass: isSync ? "text-gray-300 dark:text-gray-600" : "text-red-500 hover:bg-red-50",
                        title: isSync ? "Entegrasyon (SYNC) ile aktarılan cariler silinemez" : "Sil",
                        disabled: isSync,
                        onClick: () => handleDeleteClick(customer)
                    }
                ]}
            />
        );
    };
    // Müşteri sütun tanımları
    const customerColumns: DataTableColumn<Customer>[] = [
        { field: "id", header: "ID", width: "180px", },
        {
            field: "name",
            header: "Müşteri Adı/Şirket",
            width: "280px",
            sortable: true,
        },
        {
            field: "email",
            header: "E-posta",
            width: "280px",
            sortable: true,
        },
        {
            field: "isDealer",
            header: "Bayii",
            width: "280px",
            sortable: true,
            body: dealerBodyTemplate
        },

        {
            field: "balance",
            header: "Bakiyeler (TL/Döviz)",
            width: "280px",
            sortable: true,
            body: balanceBodyTemplate
        },
        {
            field: "actions",
            header: "İşlemler",
            width: "180px",
            align: "center",
            body: actionBodyTemplate
        },
    ];

    // 3. DataTable yerine EDataTable kullanılıyor
    return (
        <div className="h-full flex flex-col">
            {confirmModal}

            <EDataTable<Customer>
                tableKey="customer_list"
                useStore={() => store}
                dataKey="id"
                title="Müşteriler"
                placeholder="Müşteri Adı/E-posta Ara"
                // Global arama alanlarını tanımla
                searchFields={[
                    { field: "name", operator: "contains" as SimpleFilter["operator"] },
                    { field: "email", operator: "contains" as SimpleFilter["operator"] },
                ]}
                // Aksiyon butonunu tanımla
                actions={
                    <TextButton onClick={handleNew} className="btn-new">
                        Yeni Müşteri Ekle
                    </TextButton>
                }
                // Sütunları tanımla
                columns={customerColumns}


                excelModule={{
                    onExport: exportExcel,
                    onImport: importExcel
                }}
            />

            {/* Müşteri Ekleme/Düzenleme Dialogu */}
            <CustomerDialog
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                customer={customerToEdit}
                onSave={handleSaveCustomer}
            />
            <AccountStatementDialog
                isOpen={isStatementOpen}
                onClose={() => {
                    setIsStatementOpen(false);
                    setSelectedCustomer(null);
                }}
                account={selectedCustomer}
            />

            <VisitTemplateDialog
                isOpen={isRouteModalOpen}
                onClose={() => {
                    setIsRouteModalOpen(false);
                    setCustomerForRoute(null);
                }}
                employeeId={null} // Plasiyer seçimini içeriden yaptıracağız
                initialCustomer={customerForRoute}
                onSave={handleSaveNewRoute}
            />
        </div>
    );
};